import { Button, TextField } from "@mui/material";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import {
  PlatePosition,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import { useGetCompoundPlateBarcodeMutation } from "graphql/generated/schema-types";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import React, { useEffect, useState } from "react";
import { useConfirm } from "state-provider/ConfirmDialogProvider/hooks";
import styled from "styled-components";
import { CompoundPlateInfoState, NucleofectionToolActions } from "../state";
import { useNucleofectionStateProvider } from "../state/StateProvider";
import { CompoundPlate } from "./CompoundPlate";

const StyledContainerDiv = styled.div`
  height: calc(100vh - 300px);
  width: 600px;
  overflow: auto;
`;

const StyledCompoundPlateDiv = styled("div")`
  height: 320px;
  width: 100%;
`;

const StyledPlateActions = styled.div`
  width: 100%;
  margin-top: 7px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
`;

const StyledPlateSettingsContainer = styled.div`
  margin-top: 10px;
  width: 100%;
`;

export const CompoundPlateContainer = (compoundPlateTypeInfo: any) => {
  const { nucleofectionInternalState } = useNucleofectionStateProvider();
  const [selectedPlateIndex, setSelectedPlateIndex] = useState(0);
  const [uploadPlateDialogOpen, setUploadPlateDialogOpen] = useState(false);
  const dispatch = useAppReduxDispatch();
  const { isConfirmed } = useConfirm();
  const [getCompoundBarcode] = useGetCompoundPlateBarcodeMutation();

  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.sourcePlateInfo
  );
  const compoundPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.compoundPlateInfo
  );

  const handleUploadPlateClick = (index: number) => {
    setUploadPlateDialogOpen(true);
    setSelectedPlateIndex(index);
  };

  const handleRemovePlateClick = async (index: number) => {
    const confirmed = await isConfirmed(
      "Are you sure you want to remove this plate?"
    );

    if (!confirmed) {
      return;
    }
    dispatch(
      NucleofectionToolActions.CLEAR_COMPOUND_PLATE({
        index,
      })
    );
  };

  useEffect(() => {
    if (
      !sourcePlateInfo[0] ||
      sourcePlateInfo[0].plateBarcode === "" ||
      !compoundPlateInfo[0]
    ) {
      if (compoundPlateInfo[0] && compoundPlateInfo[0].plateBarcode !== "") {
        dispatch(
          NucleofectionToolActions.SET_COMPOUND_PLATE({
            index: 0,
            plateBarcode: "",
            rows: compoundPlateInfo[0].rows,
            cols: compoundPlateInfo[0].cols,
            operatingVol: compoundPlateInfo[0].operatingVol,
            labwareTypeCode: compoundPlateInfo[0].labwareTypeCode,
            wellInfo: compoundPlateInfo[0].wellInfo,
          })
        );
      }
      return;
    } else if (compoundPlateInfo[0].labwareTypeCode !== "") {
      getCompoundBarcode({
        variables: {
          sourcePlateBarcode: sourcePlateInfo[0].plateBarcode,
        },
      }).then((res) => {
        const nextCpdBarcode =
          res.data?.compoundPlateBarcode?.compoundPlateBarcode;

        if (
          nextCpdBarcode &&
          (compoundPlateInfo[0].plateBarcode === "" ||
            compoundPlateInfo[0].plateBarcode !== nextCpdBarcode)
        ) {
          dispatch(
            NucleofectionToolActions.SET_COMPOUND_PLATE({
              index: 0,
              plateBarcode: nextCpdBarcode,
              rows: compoundPlateInfo[0].rows,
              cols: compoundPlateInfo[0].cols,
              operatingVol: compoundPlateInfo[0].operatingVol,
              labwareTypeCode: compoundPlateInfo[0].labwareTypeCode,
              wellInfo: compoundPlateInfo[0].wellInfo,
            })
          );
        }
      });
    }
  }, [sourcePlateInfo, compoundPlateInfo, getCompoundBarcode, dispatch]);

  const handleCompoundPlateTypeChanged = (
    index: number,
    plateTypeInfo: any
  ) => {
    if (sourcePlateInfo[0].plateBarcode !== "") {
      dispatch(
        NucleofectionToolActions.SET_COMPOUND_PLATE({
          index,
          plateBarcode: "",
          rows: plateTypeInfo.plateRows,
          cols: plateTypeInfo.plateCols,
          operatingVol: plateTypeInfo.defaultLabwareVolume,
          labwareTypeCode: plateTypeInfo.labwareTypeCode,
          wellInfo: plateTypeInfo.labwareTypeCode,
        })
      );
    }
  };

  //TODO: Replace the div and the <b/> on the TextField component
  return (
    <StyledContainerDiv>
      {compoundPlateInfo.map((item: CompoundPlateInfoState, index: number) => (
        <div style={{ padding: "20px" }}>
          <React.Fragment>
            <TextField
              variant="outlined"
              style={{ width: "100%", marginBottom: "10px" }}
              disabled
              label="Compound Plate Barcode"
              value={item.plateBarcode}
              InputProps={{
                startAdornment: <b>{index + 1}| </b>,
              }}
            />
            <React.Fragment>
              <StyledCompoundPlateDiv>
                <CompoundPlate
                  worklistTool={WorklistTools.Nucleofection}
                  index={index}
                  wellInfo={sourcePlateInfo[index].wellInfo}
                  plateRows={item.rows}
                  plateCols={item.cols}
                />
              </StyledCompoundPlateDiv>
              <StyledPlateActions>
                <Button
                  variant="outlined"
                  onClick={() => handleUploadPlateClick(index)}
                  disabled
                >
                  Upload Plate
                </Button>
                <Button
                  variant="outlined"
                  disabled={item.rows.length < 1}
                  onClick={() => handleRemovePlateClick(index)}
                >
                  Remove Plate
                </Button>
              </StyledPlateActions>
              <StyledPlateSettingsContainer>
                <PlateType
                  platePosition={PlatePosition.Compound}
                  isDisabled={
                    nucleofectionInternalState.methodSettings!
                      .selectedSystem === 0 ||
                    sourcePlateInfo[index].rows.length === 0
                  }
                  acceptedPlateTypes={compoundPlateTypeInfo.compoundPlateInfo}
                  plateType={item.labwareTypeCode}
                  setPlateType={(plateTypeInfo) =>
                    handleCompoundPlateTypeChanged(index, plateTypeInfo)
                  }
                />
              </StyledPlateSettingsContainer>
            </React.Fragment>
          </React.Fragment>
        </div>
      ))}
    </StyledContainerDiv>
  );
};
